import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { SITE } from "@/data/site";
import { useLang } from "@/lib/i18n";

export default function FAQ({ items = [], eyebrow, title, sub, variant = "default" }) {
  const [open, setOpen] = useState(0);
  const { t } = useLang();
  if (!items.length) return null;

  const isDark = variant === "dark";
  const _eyebrow = eyebrow || t.faq.common;
  const _title = title || t.faq.straight;
  const _sub = sub || t.faq.sub;

  return (
    <section
      data-testid="faq-section"
      className={`section-padding ${
        isDark
          ? "bg-gradient-to-br from-brand-navy-dark via-slate-900 to-brand-navy-dark text-white"
          : "bg-slate-50 text-slate-900"
      }`}
    >
      <div className="container-max grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14">
        <div>
          <p className={`text-xs font-bold uppercase tracking-[0.2em] ${isDark ? "text-brand-yellow" : "text-brand-yellow-dark"}`}>
            {_eyebrow}
          </p>
          <h2 className={`font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 leading-tight ${isDark ? "text-white" : "text-slate-900"}`}>
            {_title}
          </h2>
          <p className={`mt-4 text-base sm:text-lg ${isDark ? "text-white/70" : "text-slate-600"}`}>
            {_sub}
          </p>
          <div className={`mt-6 inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${
            isDark ? "bg-white/10 text-white" : "bg-brand-yellow/20 text-slate-900 border border-brand-yellow/40"
          }`}>
            {t.common.stillQuestion}
            <a href={`tel:${SITE.primaryPhone}`} className={isDark ? "text-brand-yellow underline" : "text-brand-yellow-dark underline ml-1"}>
              {SITE.primaryPhone}
            </a>
          </div>
        </div>

        <div className="space-y-3">
          {items.map((item, idx) => {
            const isOpen = open === idx;
            return (
              <div
                key={item.q}
                data-testid={`faq-item-${idx}`}
                className={`relative overflow-hidden rounded-2xl transition-shadow border ${
                  isDark ? "bg-white/5 border-white/10 hover:bg-white/[0.07]" : "bg-white border-slate-200 hover:shadow-md"
                }`}
              >
                <span className={`absolute left-0 top-0 bottom-0 w-1 ${isOpen ? "bg-brand-yellow" : "bg-transparent"}`} />
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : idx)}
                  className="w-full text-left px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-4"
                  aria-expanded={isOpen}
                >
                  <span className={`font-heading text-base sm:text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {item.q}
                  </span>
                  <span className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                    isOpen ? "bg-brand-yellow text-slate-900" : isDark ? "bg-white/10 text-white" : "bg-slate-100 text-slate-600"
                  }`}>
                    {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                  </span>
                </button>
                {isOpen && (
                  <div className={`px-5 sm:px-6 pb-5 -mt-1 leading-relaxed whitespace-pre-line ${isDark ? "text-white/75" : "text-slate-600"}`}>
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
